'use-client';
import * as React from 'react';
import {
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import classnames from 'clsx';
import { getDataAttributes } from '@wix/editor-elements-common-utils';
import type {
  ISiteMembersInputProps,
  ITranslationKeys,
} from '../SiteMembersInput.types';
import TextInput from '../../TextInput/viewer/TextInput';
import type { ITextInputImperativeActions } from '../../TextInput/TextInput.types';
import style from './style/style.scss';
import { testIds } from './constants';

type IRefActions = {
  validate: (translations: ITranslationKeys) => boolean;
  setError: (errorMsg?: string) => void;
};

export type ISiteMembersInputRef = ITextInputImperativeActions & IRefActions;

const SiteMembersInput: React.ForwardRefRenderFunction<
  ISiteMembersInputRef,
  ISiteMembersInputProps
> = (props, ref) => {
  const {
    id,
    className,
    label,
    value = '',
    isValid,
    inputType,
    textInputId,
    autoFocus,
    placeholder,
    validationFn,
    errorMessage,
    onValueChanged,
    directionByLanguage,
    maxLength,
    borderInput = false,
    autoComplete,
    ariaDescribedBy,
  } = props;
  const inputRef = useRef<ITextInputImperativeActions>(null);
  const [shouldShowValidityIndication, setShouldShowValidityIndication] =
    useState(!isValid);
  const [inlineErrorMsg, setInlineErrorMsg] = useState<string | undefined>(
    errorMessage,
  );
  const errorMessageId = `siteMembersInputErrorMessage_${id}`;
  const handleHideValidityIndication = useCallback(() => {
    setShouldShowValidityIndication(false);
    setInlineErrorMsg(undefined);
  }, []);
  const clearErrorAndCallOnValueChanged = useCallback(
    (v: string) => {
      setShouldShowValidityIndication(false);
      setInlineErrorMsg(undefined);
      onValueChanged?.(v);
    },
    [onValueChanged],
  );
  useImperativeHandle(ref, () => {
    return {
      ...(inputRef.current as ITextInputImperativeActions),
      validate: (translations: ITranslationKeys) => {
        const errorMsg = validationFn?.(value, translations);

        if (!errorMsg) {
          setShouldShowValidityIndication(false);
          setInlineErrorMsg(undefined);
          inputRef.current!.setCustomValidity({
            type: 'message',
            message: '',
          });
          return true;
        }
        setShouldShowValidityIndication(true);
        setInlineErrorMsg(errorMsg);
        inputRef.current!.setCustomValidity({
          type: 'message',
          message: errorMsg,
        });
        inputRef.current!.focus();
        return false;
      },
      setError: (errorMsg?: string) => {
        setShouldShowValidityIndication(true);
        const message = errorMsg ? errorMsg : '';
        inputRef.current!.setCustomValidity({
          type: 'message',
          message,
        });
        if (!errorMsg) {
          return;
        }
        setInlineErrorMsg(errorMsg);
      },
    };
  });

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classnames(className, style.emailInput, {
        [style.invalid]: !borderInput && shouldShowValidityIndication,
        [style.rtl]: directionByLanguage === 'rtl',
      })}
      data-testid={testIds.container}
    >
      <TextInput
        maxLength={maxLength}
        ref={inputRef}
        id={textInputId ?? `input_${id}`}
        inputType={inputType}
        skin="AppsTextInputSkin"
        data-testid={testIds.input}
        value={value}
        label={label}
        placeholder={placeholder}
        required
        // The boolean autoComplete prop is deprecated and only supports 'on'/'off'.
        // Always set to false and use autoComplete_ for real values.
        autoComplete={false}
        // Type cast to any because TextInputAutocomplete type is too restrictive;
        // we want to support all valid HTML autocomplete values (like 'email', 'new-password', etc.)
        autoComplete_={(autoComplete as any) || 'off'}
        readOnly={false}
        isDisabled={false}
        autoFocus={autoFocus}
        shouldShowValidityIndication={shouldShowValidityIndication}
        hideValidityIndication={handleHideValidityIndication}
        onValueChange={clearErrorAndCallOnValueChanged}
        ariaAttributes={{
          describedBy: getAriaDescribedBy(
            errorMessageId,
            inlineErrorMsg,
            ariaDescribedBy,
          ),
        }}
      />
      {inlineErrorMsg && (
        <span
          id={errorMessageId}
          data-testid={testIds.inlineErrorMsg}
          className={classnames({
            [style.rtl]: directionByLanguage === 'rtl',
          })}
        >
          {inlineErrorMsg}
        </span>
      )}
    </div>
  );
};

// Helper function to determine the aria-describedby value
const getAriaDescribedBy = (
  errorId: string,
  errorMessage?: string,
  customDescribedBy?: string,
): string | undefined => {
  if (errorMessage) {
    // If there's an error message and a custom describedBy, combine them
    if (customDescribedBy) {
      return `${customDescribedBy} ${errorId}`;
    }
    // Otherwise just use the error ID
    return errorId;
  }
  // No error, just use the custom describedBy if available
  return customDescribedBy;
};

export default forwardRef(SiteMembersInput);
